package dev.wyns.ventrova.ui.cupertino

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.GridItemSpan
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dev.wyns.ventrova.app.AppMessage
import dev.wyns.ventrova.app.PlaceItem
import dev.wyns.ventrova.data.model.PlaceCategory
import dev.wyns.ventrova.data.model.VeganLevel
import dev.wyns.ventrova.resources.Res
import dev.wyns.ventrova.resources.app_name
import dev.wyns.ventrova.resources.favorites_empty
import dev.wyns.ventrova.resources.filter_all_levels
import dev.wyns.ventrova.resources.ic_add_location_alt
import dev.wyns.ventrova.resources.ic_chevron_right
import dev.wyns.ventrova.resources.ic_eco
import dev.wyns.ventrova.resources.ic_favorite
import dev.wyns.ventrova.resources.ic_favorite_filled
import dev.wyns.ventrova.resources.ic_info
import dev.wyns.ventrova.resources.ic_near_me_filled
import dev.wyns.ventrova.resources.loading
import dev.wyns.ventrova.resources.onboarding_skip
import dev.wyns.ventrova.resources.onboarding_tagline
import dev.wyns.ventrova.resources.onboarding_use_location
import dev.wyns.ventrova.resources.profile_about
import dev.wyns.ventrova.resources.profile_about_body
import dev.wyns.ventrova.resources.profile_community
import dev.wyns.ventrova.resources.profile_default_filter
import dev.wyns.ventrova.resources.profile_preferences
import dev.wyns.ventrova.resources.search_no_results
import dev.wyns.ventrova.resources.search_results
import dev.wyns.ventrova.resources.search_suggest_one
import dev.wyns.ventrova.resources.search_suggestions_title
import dev.wyns.ventrova.resources.suggest_place
import dev.wyns.ventrova.ui.components.LevelLegend
import dev.wyns.ventrova.ui.components.LevelTypeDistanceLine
import dev.wyns.ventrova.ui.components.OpenStatusLine
import dev.wyns.ventrova.ui.components.PlaceListSkeleton
import dev.wyns.ventrova.ui.components.PlaceRowSkeleton
import dev.wyns.ventrova.ui.components.PlaceThumbnail
import dev.wyns.ventrova.ui.components.SkeletonLine
import dev.wyns.ventrova.ui.components.skeleton
import dev.wyns.ventrova.ui.components.VIcon
import dev.wyns.ventrova.ui.components.bodyStyle
import dev.wyns.ventrova.ui.theme.SearchSuggestions
import dev.wyns.ventrova.ui.theme.VentrovaColors
import dev.wyns.ventrova.ui.theme.displayStyle
import dev.wyns.ventrova.ui.theme.filledIcon
import dev.wyns.ventrova.ui.theme.label
import dev.wyns.ventrova.ui.theme.palette
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource

/** Results under the native search field of the search tab. */
@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun CupertinoSearchContent(model: CupertinoModel) {
    CupertinoTheme(model) {
        val state by model.viewModel.state.collectAsState()
        LazyColumn(
            Modifier.fillMaxSize().background(VentrovaColors.Page),
            contentPadding = withSafeArea(PaddingValues(start = 16.dp, end = 16.dp, bottom = 120.dp)),
        ) {
            if (state.query.isBlank()) {
                item {
                    Text(
                        stringResource(Res.string.search_suggestions_title).uppercase(),
                        style = bodyStyle(13.sp, FontWeight.SemiBold, VentrovaColors.Muted),
                        modifier = Modifier.padding(start = 4.dp, end = 4.dp, top = 18.dp, bottom = 10.dp),
                    )
                    FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        SearchSuggestions.forEach { suggestion ->
                            Box(
                                Modifier.height(38.dp).clip(RoundedCornerShape(50)).background(Color.White).border(0.5.dp, VentrovaColors.Ink.copy(alpha = 0.12f), RoundedCornerShape(50)).clickable { model.viewModel.setQuery(suggestion) }.padding(horizontal = 15.dp),
                                contentAlignment = Alignment.Center,
                            ) {
                                Text(suggestion, style = bodyStyle(15.sp, FontWeight.Medium))
                            }
                        }
                    }
                }
            } else {
                item {
                    Text(
                        pluralStringResource(Res.plurals.search_results, state.searchResults.size, state.searchResults.size),
                        style = bodyStyle(13.sp, color = VentrovaColors.Muted),
                        modifier = Modifier.padding(start = 4.dp, end = 4.dp, top = 18.dp, bottom = 6.dp),
                    )
                    if (state.isSearching && state.searchResults.isEmpty()) {
                        PlaceListSkeleton(
                            count = 4,
                            modifier = Modifier.clip(RoundedCornerShape(22.dp)).background(Color.White),
                            loadingLabel = stringResource(Res.string.loading),
                        ) { index -> PlaceRowSkeleton(Modifier.padding(horizontal = 12.dp, vertical = 10.dp), thumbSize = 48.dp, thumbShape = RoundedCornerShape(13.dp), lines = 1, seed = index) }
                    } else if (state.searchResults.isEmpty()) {
                        Row(Modifier.padding(vertical = 30.dp, horizontal = 4.dp)) {
                            Text(stringResource(Res.string.search_no_results) + " ", style = bodyStyle(15.sp, color = VentrovaColors.Muted))
                            Text(stringResource(Res.string.search_suggest_one), style = bodyStyle(15.sp, FontWeight.SemiBold, VentrovaColors.Accent), modifier = Modifier.clickable { model.onOpenSuggest() })
                        }
                    }
                }
                items(state.searchResults, key = { it.id }) { item ->
                    val first = item == state.searchResults.first()
                    val last = item == state.searchResults.last()
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(topStart = if (first) 22.dp else 0.dp, topEnd = if (first) 22.dp else 0.dp, bottomStart = if (last) 22.dp else 0.dp, bottomEnd = if (last) 22.dp else 0.dp))
                            .background(Color.White)
                            .clickable { model.viewModel.openDetail(item.id) }
                            .padding(horizontal = 12.dp, vertical = 10.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                    ) {
                        PlaceThumbnail(item.place, Modifier.size(48.dp), RoundedCornerShape(13.dp), iconSize = 22.dp)
                        Column(Modifier.weight(1f)) {
                            Text(item.place.name, style = bodyStyle(16.sp, FontWeight.SemiBold), maxLines = 1, overflow = TextOverflow.Ellipsis)
                            LevelTypeDistanceLine(item, Modifier.padding(top = 2.dp), showCuisineInsteadOfCategory = true)
                        }
                        VIcon(Res.drawable.ic_chevron_right, size = 20.dp, tint = VentrovaColors.Chevron)
                    }
                    if (!last) HorizontalDivider(color = VentrovaColors.Ink.copy(alpha = 0.08f), thickness = 0.5.dp, modifier = Modifier.background(Color.White).padding(start = 72.dp))
                }
            }
        }
    }
}

/** Favorites as a two-column grid of cards, under the native large title. */
@Composable
internal fun CupertinoFavoritesContent(model: CupertinoModel) {
    CupertinoTheme(model) {
        val state by model.viewModel.state.collectAsState()
        LazyVerticalGrid(
            columns = GridCells.Fixed(2),
            modifier = Modifier.fillMaxSize().background(VentrovaColors.Page),
            contentPadding = withSafeArea(PaddingValues(start = 16.dp, end = 16.dp, top = 8.dp, bottom = 120.dp)),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            if (state.favoritesLoading) {
                items(4) { FavoriteCardSkeleton() }
            } else if (state.favorites.isEmpty()) {
                item(span = { GridItemSpan(2) }) {
                    Column(Modifier.padding(horizontal = 24.dp, vertical = 60.dp), horizontalAlignment = Alignment.CenterHorizontally) {
                        VIcon(Res.drawable.ic_favorite, size = 40.dp, tint = VentrovaColors.Chevron)
                        Text(stringResource(Res.string.favorites_empty), style = bodyStyle(15.sp, color = VentrovaColors.Muted), textAlign = TextAlign.Center, modifier = Modifier.padding(top = 10.dp))
                    }
                }
            }
            items(state.favorites, key = { it.id }) { item -> FavoriteCard(item, model) }
        }
    }
}

@Composable
private fun FavoriteCardSkeleton() {
    Column(Modifier.clip(RoundedCornerShape(22.dp)).background(Color.White)) {
        Box(Modifier.fillMaxWidth().height(120.dp).skeleton(RoundedCornerShape(0.dp)))
        Column(Modifier.padding(start = 12.dp, end = 12.dp, top = 12.dp, bottom = 14.dp), verticalArrangement = Arrangement.spacedBy(7.dp)) {
            SkeletonLine(width = 110.dp, height = 14.dp)
            SkeletonLine(width = 80.dp, height = 10.dp)
            SkeletonLine(width = 95.dp, height = 10.dp)
        }
    }
}

@Composable
private fun FavoriteCard(item: PlaceItem, model: CupertinoModel) {
    Column(Modifier.clip(RoundedCornerShape(22.dp)).background(Color.White).clickable { model.viewModel.openDetail(item.id) }) {
        Box(Modifier.fillMaxWidth().height(120.dp)) {
            PlaceThumbnail(item.place, Modifier.fillMaxSize(), RoundedCornerShape(0.dp), iconSize = 30.dp)
            Box(
                Modifier.align(Alignment.TopEnd).padding(8.dp).size(32.dp).clip(CircleShape).background(Color.White.copy(alpha = 0.8f)).clickable { model.viewModel.toggleFavorite(item.id) },
                contentAlignment = Alignment.Center,
            ) {
                VIcon(Res.drawable.ic_favorite_filled, size = 18.dp, tint = VentrovaColors.Favorite)
            }
        }
        Column(Modifier.padding(start = 12.dp, end = 12.dp, top = 10.dp, bottom = 12.dp)) {
            Text(item.place.name, style = bodyStyle(15.sp, FontWeight.SemiBold), maxLines = 1, overflow = TextOverflow.Ellipsis)
            LevelTypeDistanceLine(item, Modifier.padding(top = 3.dp), style = bodyStyle(12.sp, color = VentrovaColors.Secondary), glyphSize = 9.dp, showCategory = false)
            OpenStatusLine(item, Modifier.padding(top = 3.dp), style = bodyStyle(12.sp, color = VentrovaColors.Muted))
        }
    }
}

/** Profile settings as inset grouped rows, under the native large title. */
@Composable
internal fun CupertinoProfileContent(model: CupertinoModel) {
    CupertinoTheme(model) {
        Column(Modifier.fillMaxSize().background(VentrovaColors.Grouped).verticalScroll(rememberScrollState()).windowInsetsPadding(WindowInsets.safeDrawing).padding(horizontal = 16.dp)) {
            GroupTitle(stringResource(Res.string.profile_preferences))
            Group {
                GroupRow(Res.drawable.ic_eco, stringResource(Res.string.profile_default_filter), stringResource(Res.string.filter_all_levels), divider = false) {
                    model.viewModel.showMessage(AppMessage.Kind.NotAvailableYet)
                }
            }
            GroupTitle(stringResource(Res.string.profile_community))
            Group {
                GroupRow(Res.drawable.ic_add_location_alt, stringResource(Res.string.suggest_place), null, divider = true) { model.onOpenSuggest() }
                GroupRow(Res.drawable.ic_info, stringResource(Res.string.profile_about), null, divider = false, onClick = null)
            }
            Text(stringResource(Res.string.profile_about_body), style = bodyStyle(13.sp, color = VentrovaColors.Muted), modifier = Modifier.padding(start = 16.dp, end = 16.dp, top = 8.dp, bottom = 120.dp))
        }
    }
}

@Composable
private fun GroupTitle(text: String) {
    Text(text.uppercase(), style = bodyStyle(13.sp, color = VentrovaColors.Muted), modifier = Modifier.padding(start = 16.dp, end = 16.dp, top = 24.dp, bottom = 6.dp))
}

@Composable
private fun Group(content: @Composable () -> Unit) {
    Column(Modifier.fillMaxWidth().clip(RoundedCornerShape(24.dp)).background(Color.White)) { content() }
}

@Composable
private fun GroupRow(icon: DrawableResource, title: String, value: String?, divider: Boolean, onClick: (() -> Unit)?) {
    Column(Modifier.fillMaxWidth().let { if (onClick != null) it.clickable(onClick = onClick) else it }) {
        Row(Modifier.fillMaxWidth().height(52.dp).padding(horizontal = 16.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            VIcon(icon, size = 22.dp, tint = VentrovaColors.Accent)
            Text(title, style = bodyStyle(17.sp), modifier = Modifier.weight(1f))
            value?.let { Text(it, style = bodyStyle(17.sp, color = VentrovaColors.Muted)) }
            if (onClick != null) VIcon(Res.drawable.ic_chevron_right, size = 18.dp, tint = VentrovaColors.Chevron)
        }
        if (divider) HorizontalDivider(color = VentrovaColors.Ink.copy(alpha = 0.08f), thickness = 0.5.dp, modifier = Modifier.padding(start = 50.dp))
    }
}

/** First-launch screen, presented full screen once (see AppViewModel.completeOnboarding). */
@Composable
internal fun CupertinoOnboardingContent(model: CupertinoModel) {
    CupertinoTheme(model) {
        Column(Modifier.fillMaxSize().background(VentrovaColors.Page).windowInsetsPadding(WindowInsets.safeDrawing).padding(start = 24.dp, end = 24.dp, bottom = 16.dp)) {
            Box(Modifier.fillMaxWidth().height(300.dp)) {
                Box(Modifier.align(Alignment.TopCenter).offset(y = 30.dp).size(250.dp).background(dev.wyns.ventrova.ui.theme.LevelPalettes.Vegan.soft, CircleShape))
                Box(Modifier.align(Alignment.TopCenter).offset(y = 75.dp).size(160.dp).background(VentrovaColors.AccentContainerLight, CircleShape))
                Box(Modifier.align(Alignment.TopCenter).offset(y = 146.dp).size(18.dp).shadow(3.dp, CircleShape).background(Color.White, CircleShape).padding(3.dp).background(VentrovaColors.LocationIos, CircleShape))
                IllustrationCapsule(PlaceCategory.Bakery, VeganLevel.Vegan, -6f, Modifier.align(Alignment.TopStart).offset(x = 20.dp, y = 80.dp))
                IllustrationCapsule(PlaceCategory.Restaurant, VeganLevel.Vegetarian, 5f, Modifier.align(Alignment.TopEnd).offset(x = (-16).dp, y = 56.dp))
                IllustrationCapsule(PlaceCategory.Cafe, VeganLevel.VeganOptions, -3f, Modifier.align(Alignment.TopEnd).offset(x = (-62).dp, y = 196.dp))
            }
            Text(stringResource(Res.string.app_name), style = displayStyle(50, weight = FontWeight.ExtraBold, lineHeight = 0.95f), modifier = Modifier.padding(top = 22.dp))
            Text(stringResource(Res.string.onboarding_tagline), style = bodyStyle(19.sp, color = VentrovaColors.Secondary), modifier = Modifier.padding(top = 10.dp))
            LevelLegend(Modifier.padding(top = 24.dp))
            Spacer(Modifier.weight(1f))
            val env = model.environment
            Row(
                Modifier
                    .fillMaxWidth()
                    .height(56.dp)
                    .clip(RoundedCornerShape(50))
                    .background(VentrovaColors.Ink)
                    .clickable(enabled = env != null && !env.awaitingPermission) { env?.useLocationFromOnboarding() },
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                VIcon(Res.drawable.ic_near_me_filled, size = 20.dp, tint = Color.White)
                Text(stringResource(Res.string.onboarding_use_location), style = bodyStyle(17.sp, FontWeight.SemiBold, Color.White), modifier = Modifier.padding(start = 8.dp))
            }
            Box(Modifier.fillMaxWidth().padding(top = 6.dp).height(44.dp).clickable { model.viewModel.completeOnboarding(locationShared = false) }, contentAlignment = Alignment.Center) {
                Text(stringResource(Res.string.onboarding_skip), style = bodyStyle(16.sp, color = VentrovaColors.Secondary))
            }
        }
    }
}

@Composable
private fun IllustrationCapsule(category: PlaceCategory, level: VeganLevel, rotation: Float, modifier: Modifier) {
    Row(
        modifier.rotate(rotation).height(34.dp).shadow(6.dp, RoundedCornerShape(50)).background(Color.White, RoundedCornerShape(50)).padding(start = 4.dp, end = 11.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(5.dp),
    ) {
        Box(Modifier.size(27.dp).background(level.palette.color, CircleShape), contentAlignment = Alignment.Center) {
            VIcon(category.filledIcon, size = 16.dp, tint = Color.White)
        }
        Text(stringResource(category.label), style = bodyStyle(13.sp, FontWeight.Bold))
    }
}

/** Adds the safe area (navigation bar, search field, tab bar) to [padding]. */
@Composable
private fun withSafeArea(padding: PaddingValues): PaddingValues {
    val safe = WindowInsets.safeDrawing.asPaddingValues()
    return PaddingValues(
        start = padding.calculateLeftPadding(androidx.compose.ui.unit.LayoutDirection.Ltr),
        end = padding.calculateRightPadding(androidx.compose.ui.unit.LayoutDirection.Ltr),
        top = padding.calculateTopPadding() + safe.calculateTopPadding(),
        bottom = padding.calculateBottomPadding() + safe.calculateBottomPadding(),
    )
}
